import { AlertCircle, Lock, Scale, Tag, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { boxState } from "@/lib/boxing";
import BoxCube from "./BoxCube";

const STATUS = {
  ready: { Icon: Lock, textKey: "recv.box_ready", className: "text-emerald-700", tone: "border-emerald-200 bg-emerald-50/60" },
  dims: { Icon: Scale, textKey: "recv.dims_set", className: "text-blue-700", tone: "border-blue-200 bg-blue-50/60" },
  need: { Icon: AlertCircle, textKey: "recv.dims_needed", className: "text-amber-700", tone: "border-amber-200 bg-amber-50/60" },
};
const LABEL_STYLES = {
  NONE: "bg-slate-100 text-slate-500",
  READY: "bg-blue-100 text-blue-700",
  APPLIED: "bg-emerald-100 text-emerald-700",
};

// One box as a small cube. Opening and deleting are separate buttons, so nothing nests.
// `showLabel` adds the marketplace label state; the label files themselves are rows under the store's boxes.
export default function BoxTile({ box, showStatus, showLabel, onOpen, onDelete }) {
  const { t } = useLang();
  const state = boxState(box);
  const { Icon, textKey, className, tone } = STATUS[state];
  const meta = [t("recv.item_types", { count: box.item_count }), box.weight && `${box.weight} kg`].filter(Boolean).join(" · ");

  return (
    <div data-testid={`box-card-${box.id}`}
      className={`group relative flex flex-col gap-1.5 rounded-xl border p-2 pl-1.5 transition-shadow hover:shadow-md ${tone}`}>
      <button type="button" onClick={() => onOpen(box)} className="flex min-w-0 flex-1 flex-col gap-1.5 text-left">
        <span className="flex items-center gap-1.5">
          <BoxCube state={state} className="h-9 w-9 shrink-0" />
          <span className="min-w-0">
            <span className="block truncate text-sm font-extrabold leading-tight text-slate-900">{box.box_name}</span>
            <span className="block text-xs text-slate-500">{meta}</span>
          </span>
        </span>
        {showStatus && (
          <span className={`flex items-center gap-1 pl-0.5 text-[11.5px] font-bold ${className}`}>
            <Icon className="h-3.5 w-3.5" /> {t(textKey)}
          </span>
        )}
      </button>

      {showLabel && (
        <div className="border-t border-dashed border-slate-200 pt-1.5" data-testid={`label-row-${box.id}`}>
          <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11.5px] font-bold ${LABEL_STYLES[box.label_state]}`}>
            <Tag className="h-3 w-3" /> {t(`labels.state.${box.label_state}`)}
          </span>
        </div>
      )}

      {onDelete && (
        <button type="button" onClick={() => onDelete(box)} data-testid={`delete-box-${box.id}`}
          title={t("recv.delete_box")} aria-label={t("recv.delete_box")}
          className="absolute right-1 top-1 grid h-5 w-5 place-items-center rounded-full text-slate-400 opacity-0 transition-opacity hover:bg-red-100 hover:text-red-600 focus-visible:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-70">
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
